import { useEffect, useState } from 'react'
import { useAuth } from '../auth/AuthProvider'
import { LINK_AFILIACAO, PAINEL_HOTMART, linkWhatsApp } from '../indicacao/link'
import { lerLinkIndicacao, salvarLinkIndicacao } from '../indicacao/perfil'

// Indique e ganhe (spec 2026-10-11). Comissao SO em porcentagem (decisao dela).
// A Hotmart rastreia e paga; o app guarda o link e ajuda a compartilhar.
// Visual do desenho dela (13/10): cartao verde com as duas porcentagens, aviso do
// prazo, dois passos numerados e o atalho para o painel da Hotmart.
const AVISO = {
  invalido: 'Esse não parece um link da Hotmart. Copie o link de afiliado no painel da Hotmart e cole aqui.',
  sem_internet: 'Para salvar o link é preciso estar com internet.',
  erro: 'Não foi possível salvar agora. Tente de novo em instantes.',
} as const

function Icone({ children, classe = 'h-6 w-6' }: { children: React.ReactNode; classe?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={classe} fill="none" stroke="currentColor" strokeWidth={1.8}
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {children}
    </svg>
  )
}

const IconePresente = () => (
  <svg viewBox="0 0 48 48" className="h-14 w-14 shrink-0 text-emerald-300" fill="none" stroke="currentColor"
    strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="8" y="22" width="32" height="20" rx="3" />
    <rect x="6" y="15" width="36" height="7" rx="2" />
    <path d="M24 15v27" />
    <path d="M24 15c-3-7-11-8-11-3.5C13 14 18 15 24 15zM24 15c3-7 11-8 11-3.5C35 14 30 15 24 15z" />
    <path d="M9 7 7 4M16 4l-1-3M33 4l1-3" />
  </svg>
)

function Passo({ numero, titulo, texto, children }: { numero: number; titulo: string; texto: string; children: React.ReactNode }) {
  return (
    <div className="space-y-4 rounded-2xl bg-white p-4 shadow-sm">
      <div className="flex items-start gap-3">
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-teal-50 text-lg font-bold text-teal-700">
          {numero}
        </span>
        <div className="min-w-0">
          <h3 className="text-base font-bold text-slate-900">{titulo}</h3>
          <p className="text-sm text-slate-500">{texto}</p>
        </div>
      </div>
      {children}
    </div>
  )
}

export default function IndiqueEGanhe() {
  const { session } = useAuth()
  const userId = session?.user.id
  const [salvo, setSalvo] = useState<string | null>(null)
  const [carregando, setCarregando] = useState(true)
  const [editando, setEditando] = useState(false)
  const [texto, setTexto] = useState('')
  const [aviso, setAviso] = useState<string | null>(null)
  const [copiado, setCopiado] = useState<'sim' | 'falhou' | null>(null)
  const [salvando, setSalvando] = useState(false)
  // Nao carregou (sem internet): avisar, e NAO abrir o campo vazio -- quem ja
  // salvou acharia que o link sumiu (revisao final 11/10).
  const [falhaCarga, setFalhaCarga] = useState(false)
  const [tentativa, setTentativa] = useState(0)

  useEffect(() => {
    if (!userId) return
    // Saiu da tela antes de a resposta chegar: nao mexe em estado desmontado.
    let ativo = true
    setCarregando(true)
    setFalhaCarga(false)
    lerLinkIndicacao(userId)
      .then((link) => { if (ativo) { setSalvo(link); setEditando(link === null) } })
      .catch(() => { if (ativo) setFalhaCarga(true) })
      .finally(() => { if (ativo) setCarregando(false) })
    return () => { ativo = false }
  }, [userId, tentativa])

  async function salvar() {
    if (!userId || salvando) return
    setSalvando(true)
    try {
      const r = await salvarLinkIndicacao(userId, texto)
      if (r !== 'ok') { setAviso(AVISO[r]); return }
      setSalvo(texto.trim())
      setEditando(false)
      setAviso(null)
    } finally {
      setSalvando(false)
    }
  }

  async function copiar() {
    if (!salvo) return
    try { await navigator.clipboard.writeText(salvo); setCopiado('sim') } catch { setCopiado('falhou') }
  }

  const botaoPrincipal = 'flex w-full items-center justify-center gap-2 rounded-xl bg-teal-700 py-3.5 font-semibold text-white disabled:opacity-60'
  const botaoSecundario = 'rounded-xl border border-slate-300 bg-white py-2.5 text-sm font-medium text-slate-700'

  return (
    <section className="space-y-4 p-4">
      <h2 className="text-xl font-bold text-slate-900">Indique e ganhe</h2>

      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-teal-700 to-[#0f625d] p-5 text-white shadow-md">
        <div className="pointer-events-none absolute -right-10 -top-10 h-36 w-36 rounded-full bg-white/5" aria-hidden="true" />
        <div className="pointer-events-none absolute -bottom-12 right-6 h-28 w-16 rotate-[30deg] rounded-full bg-white/5" aria-hidden="true" />
        <div className="relative flex items-center gap-3">
          <IconePresente />
          <h3 className="text-2xl font-bold leading-tight">Sua indicação vale comissão</h3>
        </div>
        <p className="relative mt-3 text-[15px] text-white/90">
          Apresente o CRM a outros representantes e ganhe por cada assinatura paga.
        </p>
        <div className="relative mt-4 grid grid-cols-2 gap-3">
          <div className="rounded-2xl bg-white/10 px-2 py-3 text-center">
            <p className="text-4xl font-extrabold text-emerald-300">20%</p>
            <p className="text-sm font-medium">Plano anual</p>
          </div>
          <div className="rounded-2xl bg-white/10 px-2 py-3 text-center">
            <p className="text-4xl font-extrabold text-emerald-300">5%</p>
            <p className="text-sm font-medium">Plano mensal</p>
          </div>
        </div>
      </div>

      <div className="flex items-start gap-3 rounded-2xl bg-teal-50 px-4 py-3 text-sm text-slate-700">
        <Icone classe="mt-0.5 h-6 w-6 shrink-0 text-teal-700">
          <circle cx="12" cy="12" r="9.5" />
          <path d="M12 11v5.5M12 7.8h.01" strokeWidth={2.2} />
        </Icone>
        <p>Comissão liberada após os 30 dias grátis, quando a assinatura for paga.</p>
      </div>

      {LINK_AFILIACAO && (
        <Passo numero={1} titulo="Ative sua afiliação" texto="Cadastre-se na Hotmart para receber suas comissões.">
          <a href={LINK_AFILIACAO} target="_blank" rel="noreferrer" className={botaoPrincipal}>
            Virar afiliado na Hotmart
            <Icone classe="h-5 w-5">
              <path d="M14 4h6v6M20 4l-9 9M18 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5" />
            </Icone>
          </a>
        </Passo>
      )}

      <Passo numero={LINK_AFILIACAO ? 2 : 1} titulo="Cadastre seu link" texto="Cole o link de afiliado gerado pela Hotmart.">
        {carregando ? (
          <p className="text-sm text-slate-500">Carregando…</p>
        ) : falhaCarga ? (
          <>
            <p className="text-sm text-amber-800">Sem internet: não deu para carregar o seu link.</p>
            <button type="button" onClick={() => setTentativa((n) => n + 1)}
              className="w-full rounded-xl border border-teal-700 py-3 text-sm font-semibold text-teal-700">
              Tentar de novo
            </button>
          </>
        ) : editando ? (
          <>
            <label htmlFor="link-ind" className="sr-only">Cole aqui o seu link de afiliado</label>
            <div className="flex items-center gap-3 rounded-xl border border-slate-300 bg-white px-3 focus-within:border-teal-700">
              <Icone classe="h-6 w-6 shrink-0 text-slate-400">
                <path d="M10 14a4 4 0 0 0 5.7 0l3-3a4 4 0 0 0-5.7-5.7l-1 1" />
                <path d="M14 10a4 4 0 0 0-5.7 0l-3 3A4 4 0 0 0 11 18.7l1-1" />
              </Icone>
              <input id="link-ind" value={texto} inputMode="url" autoComplete="off" placeholder="Cole seu link aqui"
                onChange={(e) => { setTexto(e.target.value); setAviso(null) }}
                className="w-full bg-transparent py-3.5 outline-none placeholder:text-slate-400" />
            </div>
            {aviso && <p className="text-xs text-red-600">{aviso}</p>}
            <button type="button" onClick={() => void salvar()} disabled={salvando} className={botaoPrincipal}>
              {salvando ? 'Salvando…' : 'Salvar link'}
            </button>
            {salvo && (
              <button type="button" onClick={() => { setEditando(false); setAviso(null) }} disabled={salvando}
                className={`w-full ${botaoSecundario}`}>
                Cancelar
              </button>
            )}
          </>
        ) : (
          <>
            <p className="break-all rounded-xl bg-slate-50 px-3 py-3 text-sm text-slate-800" data-testid="link-salvo">{salvo}</p>
            <a href={linkWhatsApp(salvo!)} target="_blank" rel="noreferrer"
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-emerald-600 py-3.5 font-semibold text-white">
              <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor" aria-hidden="true">
                <path d="M12 2a10 10 0 0 0-8.6 15.1L2 22l5-1.3A10 10 0 1 0 12 2zm5.2 14c-.2.6-1.3 1.2-1.8 1.2-.5.1-1 .2-3.3-.7-2.8-1.2-4.6-4-4.7-4.2-.1-.2-1.1-1.5-1.1-2.8s.7-2 1-2.3c.2-.3.5-.3.7-.3h.5c.2 0 .4 0 .6.5l.8 2c.1.2.1.4 0 .5l-.4.6c-.1.2-.3.3-.1.6.2.3.8 1.3 1.7 2.1 1.2 1 2.1 1.3 2.4 1.5.3.1.5.1.6-.1l.9-1.1c.2-.3.4-.2.6-.1l2 1c.3.1.5.2.5.4.1.2.1.8-.1 1.4z" />
              </svg>
              Compartilhar no WhatsApp
            </a>
            <div className="grid grid-cols-2 gap-3">
              <button type="button" onClick={() => void copiar()} className={botaoSecundario}>
                {copiado === 'sim' ? '✓ Copiado' : 'Copiar link'}
              </button>
              <button type="button" onClick={() => { setTexto(salvo ?? ''); setEditando(true); setCopiado(null) }}
                className={botaoSecundario}>
                Trocar link
              </button>
            </div>
            {copiado === 'falhou' && (
              <p className="text-xs text-amber-800" role="status">
                Não deu para copiar. Toque e segure o link acima para copiar.
              </p>
            )}
          </>
        )}
      </Passo>

      <div className="flex items-center gap-4 rounded-2xl bg-teal-50 p-4">
        <span className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-teal-100 text-teal-700">
          <svg viewBox="0 0 24 24" className="h-8 w-8" fill="currentColor" aria-hidden="true">
            <rect x="3" y="13" width="4.5" height="8" rx="1.2" />
            <rect x="9.75" y="8" width="4.5" height="13" rx="1.2" />
            <rect x="16.5" y="3" width="4.5" height="18" rx="1.2" />
          </svg>
        </span>
        <div className="min-w-0">
          <h3 className="text-base font-bold text-slate-900">Acompanhe seus resultados</h3>
          <p className="text-sm text-slate-500">Indicações e comissões no painel da Hotmart.</p>
          <a href={PAINEL_HOTMART} target="_blank" rel="noreferrer"
            className="mt-1 inline-flex items-center gap-2 text-sm font-semibold text-teal-700">
            Abrir painel da Hotmart
            <Icone classe="h-4 w-4"><path d="M5 12h14M13 6l6 6-6 6" /></Icone>
          </a>
        </div>
      </div>
    </section>
  )
}
